import isUndefined from 'lodash/isUndefined';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import size from 'lodash/size';

import optimizelyClientInstance from './optimizely';

type FeatureFlagQuery = {
  userId: string;
  attributes: Partial<any>;
};

let lastResult: any;
let lastQuery: any = {
  userId: 'ANONYMOUS',
  attributes: undefined,
};

const OPTIMIZELY_STORAGE_KEY = 'mokaFlags';
const UNLEASH_STORAGE_KEY = 'moka:flags';

export function getFeatureFlagsFromService(
  userId = lastQuery.userId,
  attributes = lastQuery.attributes
) {
  if (!isString(userId)) {
    // eslint-disable-next-line no-param-reassign
    userId = `${userId}`;
  }

  const query = {
    userId,
  } as FeatureFlagQuery;

  // Filter undefined attributes
  const filteredAttributes = omitBy(attributes, isUndefined);
  if (size(filteredAttributes) > 0) {
    query.attributes = filteredAttributes;
  }

  if (lastResult && isEqual(lastQuery, query)) {
    return lastResult;
  }

  lastQuery = query;

  // Feature flags from Optimizely
  try {
    const flagIds = (optimizelyClientInstance as any).projectConfigManager
      .getConfig()
      .featureFlags.map((flag) => flag.key)
      .sort();
    lastResult = flagIds.map((flagId) => {
      const isEnabled = optimizelyClientInstance.isFeatureEnabled(
        flagId,
        userId,
        query.attributes ? attributes : undefined
      );

      return {
        id: flagId,
        isEnabled,
      };
    });

    localStorage.setItem('previousFlag', JSON.stringify(lastResult));

    return lastResult;
  } catch (e) {
    return JSON.parse(localStorage.getItem('previousFlag')!);
  }
}

/**
 * Retrives feature flag masks stored in session storage.
 * The mask is an object with key-value pair determining if a flag is on or off.
 *
 * @returns {Object}
 */
export function getFlagMasksFromSessionStorage() {
  const masksString = window.sessionStorage.getItem(OPTIMIZELY_STORAGE_KEY);
  if (masksString) {
    return JSON.parse(masksString);
  }
  return {};
}

/**
 * Set a feature flag to the desired value.
 *
 * Internally it sets a flag mask for the flag.
 */
export function setFeatureFlag(flagId, isEnabled) {
  const masks = getFlagMasksFromSessionStorage();
  masks[flagId] = isEnabled;
  window.sessionStorage.setItem(OPTIMIZELY_STORAGE_KEY, JSON.stringify(masks));
}

/**
 * Reset feature flags to default.
 *
 * Internal it removes all feature flag masks.
 */
export function resetFeatureFlags() {
  window.sessionStorage.removeItem(OPTIMIZELY_STORAGE_KEY);
}

/**
 * Retrives feature flag masks stored in session storage.
 * The mask is an object with key-value pair determining if a flag is on or off.
 *
 * @returns {Object}
 */
export function getUnleashFlagMasksFromSessionStorage() {
  const masksString = window.sessionStorage.getItem(UNLEASH_STORAGE_KEY);
  if (masksString) {
    return JSON.parse(masksString);
  }
  return {};
}

/**
 * Set a feature flag to the desired value.
 *
 * Internally it sets a flag mask for the flag.
 */
export function setUnleashFeatureFlag(flagId, isEnabled) {
  const masks = getUnleashFlagMasksFromSessionStorage();
  masks[flagId] = isEnabled;
  window.sessionStorage.setItem(UNLEASH_STORAGE_KEY, JSON.stringify(masks));
}

/**
 * Reset feature flags to default.
 *
 * Internal it removes all feature flag masks.
 */
export function resetUnleashFeatureFlags() {
  window.sessionStorage.removeItem(UNLEASH_STORAGE_KEY);
}

/**
 * Retrives all feature flags.
 *
 * @returns {Object[]}
 */
export function getFeatureFlags(userId?, attributes?) {
  const masks = getFlagMasksFromSessionStorage();
  const flags = getFeatureFlagsFromService(userId, attributes);

  const maskedFlags = flags.map((flag) => {
    let { isEnabled } = flag;

    // Then override with sessionStorage mask
    if (!isUndefined(masks[flag.id])) {
      isEnabled = masks[flag.id];
    }

    return {
      id: flag.id,
      isEnabled,
    };
  });

  return maskedFlags;
}

/**
 * Retrieve one feature flag object if exist.
 *
 * @param {string} id The unique id of feature flag.
 * @returns {Object}
 */
export function getFeatureFlag(id) {
  const flags = getFeatureFlags();
  const flag = flags.find((f) => f.id === id);
  if (!flag) {
    throw new Error(`Feature flag '${id}' does not exist`);
  }
  return flag;
}

/**
 * True if a feature with the unique id is enabled.
 *
 * @param {string} id The unique id of feature flag.
 * @returns {boolean}
 */
export function hasFeature(id) {
  const flag = getFeatureFlag(id);
  return flag && flag.isEnabled;
}

export const unleashHasFeature = (id: string) => (state) =>
  state.unleashFeatureFlags.items[id] || {
    id,
    isEnabled: false,
  };
